/**
 * Skill builder wizard (web): replaces the old 3-field create form.
 *
 * Two-way skills (docs/skills-two-way-design.md):
 *   Basics → Type → [custom CV only: Style → Sections] → Review & create.
 * Portable skills keep an instructions editor on the Type step; custom CV
 * skills pick a style preset, one of the 6 curated accents, a photo toggle
 * (live mini CV preview) and the ordered section include-list with per-section
 * prompts. The SKILL.md is composed server-side from the structured body.
 *
 * Also drives EDIT mode (PATCH /api/skills/:skillId) when `skillId` +
 * `initialDraft` are provided (detail page "Edit").
 */
import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { COLOR_MAP, LAYOUT_MAP, isColorDark } from '@tabbio/tokens';
import type { CVAccentColor, CVLayoutStyle, CvSectionId, SkillKind } from '@tabbio/types';
import { HugeSymbol as MaterialSymbol } from '../icons/HugeSymbol';
import { WebDialog } from '../common/WebDialog.web';
import { confirm } from '../../lib/alert.web';
import { useToast } from '../../hooks/useToast';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { useLocale } from '../../hooks/useLocale';
import {
  createSkill,
  generateSkillSectionPrompt,
  updateSkill,
  isEntitlementDenied,
  type SkillEntitlementDecision,
} from '../../lib/skills-api';
import {
  INSTRUCTIONS_MAX,
  SECTION_PROMPT_MAX,
  SKILL_LANGUAGE_OPTIONS,
  SKILL_LEVEL_OPTIONS,
  SKILL_REGION_OPTIONS,
  addableSectionIds,
  defaultBuilderDraft,
  draftToSkillInput,
  enabledSections,
  type SkillBuilderDraft,
} from './skill-builder-shared';
import {
  AudienceDropdown,
  ModelPicker,
  SkillIconPicker,
} from './skill-builder-fields.web';
import { NativeButton } from '../common/NativeButton';

interface Props {
  onClose: () => void;
  /**
   * Called after a successful create (or save in edit mode). Create passes the
   * new skill id so the caller can land on its detail page.
   */
  onCreated: (createdSkillId?: string) => void;
  /** Edit mode: PATCH this skill instead of creating a new one. */
  skillId?: string;
  initialDraft?: SkillBuilderDraft;
  /** Preset by the New skill chooser: fixes the skill kind and reflows steps. */
  presetKind?: SkillKind;
  /** Carried in from the CV "describe" screen: pre-fills the description. */
  seedDescription?: string;
}

type StepId = 'basics' | 'type' | 'style' | 'sections' | 'review';

const LAYOUT_IDS: CVLayoutStyle[] = ['MODERN', 'CLASSIC', 'EXECUTIVE', 'CREATIVE'];
const COLOR_IDS: CVAccentColor[] = ['BLACK', 'BLUE', 'PURPLE', 'EMERALD', 'RED', 'ORANGE'];

function entitlementMode(
  decision: SkillEntitlementDecision | undefined,
): 'individual' | 'employer' | undefined {
  const set = decision?.recommendedPlanSet;
  return set === 'employer' ? 'employer' : set ? 'individual' : undefined;
}

export function CreateSkillDialog({
  onClose,
  onCreated,
  skillId,
  initialDraft,
  presetKind,
  seedDescription,
}: Props) {
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const toast = useToast();
  const upgradeModal = useUpgradeModal();
  const isEdit = Boolean(skillId);

  // Compute the starting draft once: a fresh CV create seeds the kind, the
  // description carried in from the "describe" screen, and default section
  // prompts (so the wizard looks populated, matching the reference).
  const [initialDraftValue] = useState<SkillBuilderDraft>(() => {
    if (initialDraft) return initialDraft;
    const base = defaultBuilderDraft();
    if (presetKind) base.kind = presetKind;
    if (seedDescription) base.description = seedDescription;
    if (base.kind === 'cv_custom') {
      base.region = base.region || 'UAE';
      base.level = base.level || 'fresh-grad';
    }
    return base;
  });
  const [draft, setDraft] = useState<SkillBuilderDraft>(initialDraftValue);
  // Snapshot of the starting draft: any change away from it marks the wizard
  // dirty, so a stray backdrop click can't silently discard a long draft.
  const [initialDraftJson] = useState(() => JSON.stringify(initialDraftValue));
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [basicsTouched, setBasicsTouched] = useState(false);
  const isDirty = JSON.stringify(draft) !== initialDraftJson;
  // Once a submit succeeds the draft is persisted: any close after that must
  // never ask to "discard". Also guards the browser dispatching the submit
  // click on an ancestor when the button re-renders mid-press.
  const submittedRef = useRef(false);

  /** Backdrop / close / cancel path: confirms before discarding a dirty draft. */
  const requestClose = async () => {
    if (submitting) return;
    if (isDirty && !submittedRef.current) {
      const discard = await confirm({
        title: t('skills.discard.title'),
        message: t('skills.discard.message'),
        confirmLabel: t('skills.discard.confirm'),
        cancelLabel: t('actions.cancel'),
        destructive: true,
      });
      if (!discard) return;
    }
    onClose();
  };

  // CV skills reflow to the reference's 3 steps (basics → sections → style);
  // portable skills (edit only, since the New skill flow routes them to the
  // any-skill dialog) keep the instructions + review path.
  const steps = useMemo<StepId[]>(
    () =>
      draft.kind === 'cv_custom'
        ? ['basics', 'sections', 'style']
        : ['basics', 'type', 'review'],
    [draft.kind],
  );
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const isLast = stepIndex === steps.length - 1;

  const patch = (partial: Partial<SkillBuilderDraft>) =>
    setDraft((prev) => ({ ...prev, ...partial }));

  const nameError =
    basicsTouched && !draft.name.trim()
      ? t('skills.builder.basics.nameRequired')
      : '';
  const descError =
    basicsTouched && !draft.description.trim()
      ? t('skills.builder.basics.descriptionRequired')
      : '';

  const sectionsValid =
    draft.kind !== 'cv_custom' || enabledSections(draft).length > 0;

  const stepValid =
    step === 'basics'
      ? draft.kind === 'cv_custom'
        ? Boolean(draft.name.trim())
        : Boolean(draft.name.trim() && draft.description.trim())
      : step === 'sections'
        ? sectionsValid
        : true;

  const goNext = () => {
    if (step === 'basics' && !stepValid) {
      setBasicsTouched(true);
      return;
    }
    if (!stepValid) return;
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const input = draftToSkillInput(draft);
      let createdSkillId: string | undefined;
      if (isEdit && skillId) {
        await updateSkill(skillId, input);
        toast.success(t('skills.builder.saved'));
      } else {
        const created = await createSkill(input);
        createdSkillId = created.skillId;
        toast.success(t('skills.builder.created'));
      }
      submittedRef.current = true;
      onCreated(createdSkillId);
      onClose();
    } catch (error) {
      if (isEntitlementDenied(error)) {
        upgradeModal.open(entitlementMode(error.decision));
        onClose();
        return;
      }
      toast.error(
        t('skills.builder.failed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const dialogTitle = isEdit
    ? t('skills.builder.editTitle')
    : t('skills.builder.title');
  const dialogSubtitle = `${t('skills.builder.stepLabel', {
    current: stepIndex + 1,
    total: steps.length,
  })} · ${t(`skills.builder.steps.${step}`)}`;
  const dialogFooter = (
    <div style={footerActions} dir={isRTL ? 'rtl' : 'ltr'}>
      {stepIndex > 0 ? (
        <NativeButton
          title={t('skills.builder.back')}
          onPress={goBack}
          disabled={submitting}
          variant="outline"
          size="lg"
          pill
          iconLeft={<MaterialSymbol name={isRTL ? 'arrow_forward' : 'arrow_back'} size={17} />}
          style={{ flex: 1 }}
        />
      ) : (
        <NativeButton
          title={t('actions.cancel')}
          onPress={() => void requestClose()}
          disabled={submitting}
          variant="outline"
          size="lg"
          pill
          style={{ flex: 1 }}
        />
      )}
      {isLast ? (
        <NativeButton
          title={
            submitting
              ? t('skills.builder.saving')
              : isEdit
                ? t('skills.builder.save')
                : t('skills.builder.create')
          }
          onPress={() => void handleSubmit()}
          loading={submitting}
          color="contrast"
          size="lg"
          pill
          style={{ flex: 1 }}
        />
      ) : (
        <NativeButton
          title={t('skills.builder.next')}
          onPress={goNext}
          disabled={!stepValid}
          color="contrast"
          size="lg"
          pill
          iconRight={<MaterialSymbol name={isRTL ? 'arrow_back' : 'arrow_forward'} size={18} />}
          style={{ flex: 1 }}
        />
      )}
    </div>
  );

  return (
    <WebDialog
      title={dialogTitle}
      subtitle={dialogSubtitle}
      onClose={() => void requestClose()}
      maxWidth={680}
      footer={dialogFooter}
      zIndex={9999}
    >
      <div dir={isRTL ? 'rtl' : 'ltr'}>
        <div style={dotsRow} aria-hidden>
          {steps.map((s, i) => (
            <span
              key={s}
              style={{
                height: 4,
                flex: 1,
                borderRadius: 2,
                background:
                  i <= stepIndex ? 'var(--text-primary)' : 'var(--input-border)',
                transition: 'background 150ms',
              }}
            />
          ))}
        </div>

        {step === 'basics' ? (
          draft.kind === 'cv_custom' ? (
            <StepCvBasics
              t={t}
              draft={draft}
              patch={patch}
              nameError={nameError}
              isRTL={isRTL}
              onUpgrade={() => upgradeModal.open('individual')}
            />
          ) : (
            <StepBasics
              t={t}
              draft={draft}
              patch={patch}
              nameError={nameError}
              descError={descError}
            />
          )
        ) : null}
        {step === 'type' ? <StepType t={t} draft={draft} patch={patch} /> : null}
        {step === 'style' ? <StepStyle t={t} draft={draft} patch={patch} /> : null}
        {step === 'sections' ? (
          <StepSections
            t={t}
            draft={draft}
            setDraft={setDraft}
            sectionsValid={sectionsValid}
            onUpgrade={() => upgradeModal.open('individual')}
          />
        ) : null}
        {step === 'review' ? <StepReview t={t} draft={draft} /> : null}
      </div>
    </WebDialog>
  );
}

// ── Steps ─────────────────────────────────────────────────────────────────────

type TFn = TFunction;

function StepBasics({
  t,
  draft,
  patch,
  nameError,
  descError,
}: {
  t: TFn;
  draft: SkillBuilderDraft;
  patch: (partial: Partial<SkillBuilderDraft>) => void;
  nameError: string;
  descError: string;
}) {
  return (
    <div style={col16}>
      {/* Image + name. A portable skill is name, image and description - it
          carries no audience, so there are no region/level/language selects
          here (the catalog treats an undeclared facet as unscoped). */}
      <Field label={t('skills.builder.basics.nameLabel')} error={nameError}>
        {/* The label spans the row so the icon trigger and the input share one
            baseline and one height instead of the trigger hanging beside the
            label. */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <SkillIconPicker
            value={draft.iconKey}
            region={draft.region}
            iconUrl={draft.iconUrl}
            onChange={(icon) => patch({ iconKey: icon })}
            onImageChange={(url) => patch({ iconUrl: url })}
          />
          <input
            value={draft.name}
            dir="auto"
            onChange={(e) => patch({ name: e.target.value })}
            placeholder={t('skills.builder.basics.namePlaceholder')}
            style={{ ...inputStyle(Boolean(nameError)), flex: 1, minWidth: 0 }}
          />
        </div>
      </Field>
      <Field label={t('skills.builder.basics.descriptionLabel')} error={descError}>
        <textarea
          value={draft.description}
          dir="auto"
          onChange={(e) => patch({ description: e.target.value })}
          placeholder={t('skills.builder.basics.descriptionPlaceholder')}
          rows={2}
          style={textareaStyle(Boolean(descError), 64)}
        />
      </Field>

      {/* No tags field: the AI drafts them (generateDraft returns `tags`) and
          they stay on the draft so search keeps working - the author just
          never has to write them. */}
    </div>
  );
}

function StepCvBasics({
  t,
  draft,
  patch,
  nameError,
  isRTL,
  onUpgrade,
}: {
  t: TFn;
  draft: SkillBuilderDraft;
  patch: (partial: Partial<SkillBuilderDraft>) => void;
  nameError: string;
  isRTL: boolean;
  onUpgrade: () => void;
}) {
  const regionOpts = SKILL_REGION_OPTIONS.map((r) => ({
    value: r,
    label: t(`skills.newFlow.regions.${r}`),
  }));
  const levelOpts = SKILL_LEVEL_OPTIONS.map((l) => ({
    value: l,
    label: t(`skills.newFlow.levels.${l}`),
  }));
  const langOpts = SKILL_LANGUAGE_OPTIONS.map((l) => ({
    value: l,
    label: t(`skills.newFlow.langs.${l}`),
  }));

  return (
    <div style={col16}>
      <p style={{ ...hintText, margin: 0 }}>{t('skills.newFlow.basics.subtitle')}</p>

      {/* Icon + name */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <SkillIconPicker
          value={draft.iconKey}
          region={draft.region}
          iconUrl={draft.iconUrl}
          onChange={(icon) => patch({ iconKey: icon })}
          onImageChange={(url) => patch({ iconUrl: url })}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            value={draft.name}
            dir="auto"
            onChange={(e) => patch({ name: e.target.value })}
            placeholder={t('skills.newFlow.basics.nameCvPlaceholder')}
            style={underlineInput(Boolean(nameError), 20)}
          />
          {nameError ? <div style={errorText}>{nameError}</div> : null}
        </div>
      </div>

      {/* Description (tags are AI-drafted, never hand-written) */}
      <input
        value={draft.description}
        dir="auto"
        onChange={(e) => patch({ description: e.target.value })}
        placeholder={t('skills.builder.basics.descriptionLabel')}
        style={underlineInput(false, 15)}
      />

      {/* Audience */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <AudienceDropdown
          value={draft.region || regionOpts[0].value}
          options={regionOpts}
          onChange={(v) => patch({ region: v })}
          isRTL={isRTL}
        />
        <AudienceDropdown
          value={draft.level || levelOpts[1].value}
          options={levelOpts}
          onChange={(v) => patch({ level: v })}
          isRTL={isRTL}
        />
        <AudienceDropdown
          value={draft.language}
          options={langOpts}
          onChange={(v) => patch({ language: v })}
          isRTL={isRTL}
        />
      </div>

      {/* Recommended model */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>
            {t('skills.newFlow.basics.recommendedModel')}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            {t('skills.newFlow.basics.recommendedModelHint')}
          </span>
        </div>
        <ModelPicker
          value={draft.model}
          onChange={(id) => patch({ model: id })}
          onUpgrade={onUpgrade}
          suggestion={draft.recommendedModelSuggestion}
        />
      </div>
    </div>
  );
}

function StepType({
  t,
  draft,
  patch,
}: {
  t: TFn;
  draft: SkillBuilderDraft;
  patch: (partial: Partial<SkillBuilderDraft>) => void;
}) {
  const instructionsCount = draft.instructions.length;
  return (
    <div style={col16}>
      <div style={fieldLabel}>{t('skills.builder.type.label')}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <TypeCard
          active={draft.kind === 'default'}
          icon="grid_view"
          title={t('skills.builder.type.portableTitle')}
          body={t('skills.builder.type.portableBody')}
          onClick={() => patch({ kind: 'default' })}
        />
        <TypeCard
          active={draft.kind === 'cv_custom'}
          icon="description"
          title={t('skills.builder.type.customTitle')}
          body={t('skills.builder.type.customBody')}
          onClick={() => patch({ kind: 'cv_custom' })}
        />
      </div>

      {draft.kind === 'default' ? (
        <Field label={t('skills.builder.type.instructionsLabel')}>
          <textarea
            value={draft.instructions}
            dir="auto"
            maxLength={INSTRUCTIONS_MAX}
            onChange={(e) => patch({ instructions: e.target.value })}
            placeholder={t('skills.builder.type.instructionsPlaceholder')}
            rows={9}
            style={textareaStyle(false, 200)}
          />
          <div style={metaRow}>
            <span style={hintText}>{t('skills.builder.type.instructionsHint')}</span>
            <span style={countText}>
              {t('skills.builder.type.charCount', {
                used: instructionsCount,
                max: INSTRUCTIONS_MAX,
              })}
            </span>
          </div>
        </Field>
      ) : null}
    </div>
  );
}

function StepStyle({
  t,
  draft,
  patch,
}: {
  t: TFn;
  draft: SkillBuilderDraft;
  patch: (partial: Partial<SkillBuilderDraft>) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ ...col16, flex: '1 1 280px', minWidth: 0 }}>
        {/* Layout preset */}
        <div>
          <div style={fieldLabel}>{t('skills.builder.style.layoutLabel')}</div>
          {/* Each option shows a static thumbnail of the layout in the chosen
              accent, so the pick is made by eye rather than by name. */}
          {/* Thumbnail-sized columns: four across in the full-width dialog,
              two on a phone, never a poster-sized card. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {LAYOUT_IDS.map((layout) => {
              const active = draft.style === layout;
              return (
                <button
                  key={layout}
                  type="button"
                  onClick={() => patch({ style: layout })}
                  aria-pressed={active}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    padding: 10,
                    borderRadius: 16,
                    border: active ? '2px solid var(--text-primary)' : '1px solid var(--input-border)',
                    background: 'var(--input-bg)',
                    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <LayoutThumb layout={layout} accent={COLOR_MAP[draft.color]} />
                  {t(`skills.builder.style.layouts.${layout}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Accent color: the 6 curated CV accents (color = user's choice, allowed) */}
        <div>
          <div style={fieldLabel}>{t('skills.builder.style.colorLabel')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {COLOR_IDS.map((colorId) => {
              const isActive = draft.color === colorId;
              return (
                <button
                  key={colorId}
                  type="button"
                  onClick={() => patch({ color: colorId })}
                  aria-label={t(`skills.builder.style.colors.${colorId}`)}
                  aria-pressed={isActive}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: 'none',
                    outline: isActive
                      ? '2px solid var(--text-primary)'
                      : '1px solid var(--gem-outline)',
                    outlineOffset: isActive ? 2 : 0,
                    cursor: 'pointer',
                    backgroundColor: COLOR_MAP[colorId],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'outline-color 150ms, outline-offset 150ms',
                  }}
                >
                  {isActive ? (
                    <MaterialSymbol name="check" size={16} style={{ color: '#fff' }} />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Photo toggle and the live preview were both cut in review. The
            style step is layout + accent only now. `showPhoto` still ships on
            every submit (skillCvConfigSchema requires it) - it is read from
            the draft, never hardcoded, so editing a saved skill round-trips
            its stored value instead of silently flipping the photo back on. */}
      </div>
    </div>
  );
}

function StepSections({
  t,
  draft,
  setDraft,
  sectionsValid,
  onUpgrade,
}: {
  t: TFn;
  draft: SkillBuilderDraft;
  setDraft: Dispatch<SetStateAction<SkillBuilderDraft>>;
  sectionsValid: boolean;
  onUpgrade: () => void;
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [dragEnabled, setDragEnabled] = useState<number | null>(null);
  const toast = useToast();
  const [suggesting, setSuggesting] = useState<string | null>(null);

  const reorder = (from: number, to: number) => {
    setDraft((prev) => {
      if (from === to || to < 0 || to >= prev.sections.length) return prev;
      const next = [...prev.sections];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...prev, sections: next };
    });
  };
  const move = (index: number, delta: -1 | 1) => reorder(index, index + delta);
  const toggle = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, enabled: !section.enabled } : section,
      ),
    }));
  };
  const setPrompt = (index: number, prompt: string) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, prompt } : section,
      ),
    }));
  };
  // No AI endpoint yet: "Suggest" fills the field with the curated default
  // prompt for that section, after a short spinner so it reads as generation.
  const suggest = (index: number, id: string) => {
    setSuggesting(id);
    const applyPrompt = (value: string) => {
      setDraft((prev) => ({
        ...prev,
        sections: prev.sections.map((section, i) =>
          i === index ? { ...section, prompt: value } : section,
        ),
      }));
    };
    void generateSkillSectionPrompt({
      sectionId: id as CvSectionId,
      skillName: draft.name.trim() || undefined,
      skillPrompt: draft.description.trim() || undefined,
      // Present means "regenerate, differently".
      currentPrompt: draft.sections[index]?.prompt.trim() || undefined,
      otherSections: draft.sections.filter((x) => x.enabled).map((x) => x.id),
    })
      .then((result) => applyPrompt(result.prompt))
      .catch((error: unknown) => {
        if (isEntitlementDenied(error)) {
          onUpgrade();
          return;
        }
        // Never dead-end the button: fall back to the curated default, which
        // is what this control used to return unconditionally - but say so,
        // or a 500 reads as a successful suggestion.
        toast.error(t('skills.builder.sectionsStep.suggestFailed'));
        applyPrompt(
          t(`skills.newFlow.sectionDefaults.${id}`, {
            defaultValue: t(`skills.builder.sectionsStep.prompts.${id}`),
          }),
        );
      })
      .finally(() => setSuggesting(null));
  };
  const addable = addableSectionIds(draft);
  const nextAddable = addable[0];
  const addSection = () => {
    if (!nextAddable) return;
    setDraft((prev) => ({
      ...prev,
      sections: [...prev.sections, { id: nextAddable, enabled: true, prompt: '' }],
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ ...hintText, margin: 0 }}>{t('skills.builder.sectionsStep.hint')}</p>
      {!sectionsValid ? (
        <span style={errorText}>{t('skills.builder.sectionsStep.needOne')}</span>
      ) : null}
      {draft.sections.map((section, index) => {
        const isSuggesting = suggesting === section.id;
        const hasPrompt = section.prompt.trim().length > 0;
        return (
          <div
            key={section.id}
            draggable={dragEnabled === index}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => {
              e.preventDefault();
              if (overIndex !== index) setOverIndex(index);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIndex !== null) reorder(dragIndex, index);
              setDragIndex(null);
              setOverIndex(null);
              setDragEnabled(null);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
              setDragEnabled(null);
            }}
            style={{
              ...sectionRowCard(section.enabled),
              outline:
                overIndex === index && dragIndex !== null && dragIndex !== index
                  ? '2px solid var(--text-primary)'
                  : 'none',
              opacity: dragIndex === index ? 0.5 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                role="checkbox"
                aria-checked={section.enabled}
                aria-label={t(`skills.builder.sectionsStep.names.${section.id}`)}
                onClick={() => toggle(index)}
                style={checkBtn(section.enabled)}
              >
                {section.enabled ? <MaterialSymbol name="check" size={15} /> : null}
              </button>
              <span
                style={{
                  flex: 1,
                  fontSize: 15,
                  fontWeight: 500,
                  color: section.enabled ? 'var(--text-primary)' : 'var(--text-tertiary)',
                }}
              >
                {t(`skills.builder.sectionsStep.names.${section.id}`)}
              </span>
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={t('skills.builder.sectionsStep.moveUp')}
                style={arrowBtn(index === 0)}
              >
                <MaterialSymbol name="arrow_upward" size={17} />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === draft.sections.length - 1}
                aria-label={t('skills.builder.sectionsStep.moveDown')}
                style={arrowBtn(index === draft.sections.length - 1)}
              >
                <MaterialSymbol name="arrow_downward" size={17} />
              </button>
              <span
                aria-hidden
                title={t('skills.builder.sectionsStep.dragHint')}
                onMouseDown={() => setDragEnabled(index)}
                onMouseUp={() => setDragEnabled(null)}
                onMouseLeave={() => setDragEnabled((d) => (d === index ? null : d))}
                style={{
                  display: 'inline-flex',
                  color: 'var(--text-tertiary)',
                  cursor: 'grab',
                  flexShrink: 0,
                  touchAction: 'none',
                }}
              >
                <MaterialSymbol name="drag_indicator" size={18} />
              </span>
            </div>
            {section.enabled ? (
              <div>
                {/* While a prompt is being drafted the field dims and the pill
                    turns into a spinner + "Suggesting…", so the wait is visible
                    instead of text simply appearing. */}
                <textarea
                  value={section.prompt}
                  dir="auto"
                  maxLength={SECTION_PROMPT_MAX}
                  readOnly={isSuggesting}
                  onChange={(e) => setPrompt(index, e.target.value)}
                  placeholder={t(`skills.builder.sectionsStep.prompts.${section.id}`)}
                  rows={2}
                  style={{
                    ...textareaStyle(false, 52),
                    fontSize: 14,
                    opacity: isSuggesting ? 0.55 : 1,
                    transition: 'opacity 150ms',
                  }}
                />
                {/* The AI control lives under the field, as on native: floating
                    it inside the textarea collided with the resize handle and
                    the character count. */}
                <div style={sectionFootRow}>
                  <button
                    type="button"
                    onClick={() => suggest(index, section.id)}
                    disabled={isSuggesting}
                    aria-busy={isSuggesting}
                    style={{ ...suggestBtn, cursor: isSuggesting ? 'progress' : 'pointer' }}
                  >
                    <MaterialSymbol
                      name={
                        isSuggesting
                          ? 'progress_activity'
                          : hasPrompt
                            ? 'refresh'
                            : 'lightbulb'
                      }
                      size={15}
                      style={
                        isSuggesting
                          ? { animation: 'tabbio-spinner-spin 0.8s linear infinite' }
                          : undefined
                      }
                    />
                    {isSuggesting
                      ? t('skills.builder.sectionsStep.suggesting')
                      : hasPrompt
                        ? t('skills.builder.sectionsStep.regenerate')
                        : t('skills.builder.sectionsStep.suggest')}
                  </button>
                  {section.prompt.length > 0 ? (
                    <span style={countText}>
                      {t('skills.builder.sectionsStep.promptCount', {
                        used: section.prompt.length,
                        max: SECTION_PROMPT_MAX,
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      {nextAddable ? (
        <button type="button" onClick={addSection} style={addSectionBtn}>
          <MaterialSymbol name="add" size={18} style={{ color: 'var(--text-secondary)' }} />
          {t('skills.builder.sectionsStep.addSection', {
            name: t(`skills.builder.sectionsStep.names.${nextAddable}`),
          })}
        </button>
      ) : null}
    </div>
  );
}

function StepReview({ t, draft }: { t: TFn; draft: SkillBuilderDraft }) {
  const isCustom = draft.kind === 'cv_custom';
  const included = enabledSections(draft);
  return (
    <div style={col16}>
      <div style={groupCard}>
        <ReviewRow label={t('skills.builder.review.name')} value={draft.name.trim()} />
        <ReviewRow
          label={t('skills.builder.review.description')}
          value={draft.description.trim()}
        />
        <ReviewRow
          label={t('skills.builder.review.type')}
          value={
            isCustom
              ? t('skills.builder.review.typeCustom')
              : t('skills.builder.review.typePortable')
          }
        />
        {isCustom ? (
          <>
            <ReviewRow
              label={t('skills.builder.review.style')}
              value={t(`skills.builder.style.layouts.${draft.style}`)}
            />
            <ReviewRow
              label={t('skills.builder.review.color')}
              value={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: COLOR_MAP[draft.color],
                      display: 'inline-block',
                    }}
                  />
                  {t(`skills.builder.style.colors.${draft.color}`)}
                </span>
              }
            />
            <ReviewRow
              label={t('skills.builder.review.sections')}
              value={
                <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {included.map((section, i) => (
                    <span key={section.id} style={{ fontSize: 14 }}>
                      {i + 1}. {t(`skills.builder.sectionsStep.names.${section.id}`)}
                      {section.prompt.trim() ? (
                        <span style={{ color: 'var(--text-tertiary)' }}>
                          {' '}
                         : {t('skills.builder.review.withPrompt')}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </span>
              }
            />
          </>
        ) : (
          <ReviewRow
            label={t('skills.builder.review.instructions')}
            value={
              draft.instructions.trim() ? (
                <span
                  dir="auto"
                  style={{
                    display: 'block',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 140,
                    overflowY: 'auto',
                    fontSize: 13,
                    lineHeight: '19px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {draft.instructions.trim()}
                </span>
              ) : (
                <span style={{ color: 'var(--text-tertiary)' }}>
                  {t('skills.builder.review.noInstructions')}
                </span>
              )
            }
          />
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <MaterialSymbol
          name="lock"
          size={16}
          style={{ color: 'var(--text-tertiary)', marginTop: 1 }}
        />
        <p style={{ ...hintText, margin: 0 }}>{t('skills.builder.review.privateNote')}</p>
      </div>
    </div>
  );
}

// ── Mini CV preview (Style step) ─────────────────────────────────────────────
// The preview is a CV "paper", so it deliberately stays white in both themes.


/**
 * A static, wordless miniature of one CV layout in the chosen accent: header,
 * then summary, experience and skills blocks, drawn from the same layout tokens
 * the real renderer reads (`LAYOUT_MAP`). It is a picture of the template, not
 * of this skill's sections, so every card in the grid reads the same way.
 */
function LayoutThumb({ layout, accent }: { layout: CVLayoutStyle; accent: string }) {
  const tokens = LAYOUT_MAP[layout];
  const headerColored = tokens.hasColoredHeader;
  const headerFg = headerColored
    ? isColorDark(accent)
      ? '#ffffff'
      : '#1D1D1F'
    : tokens.nameUsesAccent
      ? accent
      : '#1D1D1F';
  const bar = (width: string | number, height: number, background: string, opacity = 1): CSSProperties => ({
    display: 'block',
    width,
    height,
    borderRadius: 2,
    background,
    opacity,
  });

  return (
    <span
      aria-hidden
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: 86,
        height: 110,
        borderRadius: 6,
        background: '#ffffff',
        border: '1px solid rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: '8px 8px 6px',
          background: headerColored ? accent : '#ffffff',
        }}
      >
        <span style={bar('62%', 6, headerFg, 0.9)} />
        <span style={bar('44%', 3, headerColored ? headerFg : 'rgba(0,0,0,0.3)', headerColored ? 0.7 : 1)} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 8px 8px', flex: 1, overflow: 'hidden' }}>
        {(['summary', 'experience', 'skills'] as const).map((section) => (
          <span key={section} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {tokens.sectionHeaderHasBar ? <span style={bar(2, 6, accent)} /> : null}
              <span style={bar(30, 4, tokens.sectionHeaderUsesAccent ? accent : 'rgba(0,0,0,0.55)')} />
            </span>
            {tokens.sectionHeaderBorderUsesAccent ? <span style={bar('100%', 1, accent, 0.6)} /> : null}
            {section === 'skills' && tokens.skillsAsPills ? (
              <span style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[16, 22, 14].map((w, i) => (
                  <span
                    key={i}
                    style={{
                      ...bar(w, 5, tokens.skillPillUsesAccent ? accent : 'rgba(0,0,0,0.08)', tokens.skillPillUsesAccent ? 0.85 : 1),
                      borderRadius: 3,
                    }}
                  />
                ))}
              </span>
            ) : (
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={bar('92%', 2.5, 'rgba(0,0,0,0.14)')} />
                <span style={bar('76%', 2.5, 'rgba(0,0,0,0.14)')} />
              </span>
            )}
          </span>
        ))}
      </span>
    </span>
  );
}

// ── Small pieces ──────────────────────────────────────────────────────────────

function TypeCard({
  active,
  icon,
  title,
  body,
  onClick,
}: {
  active: boolean;
  icon: string;
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: '1 1 220px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '16px 16px 14px',
        borderRadius: 16,
        border: active ? '2px solid var(--text-primary)' : '1px solid var(--input-border)',
        background: active ? 'rgba(0,0,0,0.04)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'start',
        fontFamily: 'inherit',
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.06)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MaterialSymbol name={icon} size={18} style={{ color: 'var(--text-secondary)' }} />
      </span>
      <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>{title}</span>
      <span style={{ fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
        {body}
      </span>
    </button>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label style={fieldLabel}>{label}</label>
      {children}
      {error ? <span style={errorText}>{error}</span> : null}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ fontSize: 14, color: 'var(--text-primary)' }} dir="auto">
        {value}
      </span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const dotsRow: CSSProperties = {
  display: 'flex',
  gap: 6,
  paddingBottom: 18,
};

const footerActions: CSSProperties = {
  display: 'flex',
  gap: 10,
};

const col16: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };

const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 8,
};

const hintText: CSSProperties = {
  margin: 0,
  fontSize: 12.5,
  lineHeight: '18px',
  color: 'var(--text-tertiary)',
};

const countText: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-tertiary)',
  flexShrink: 0,
};

const metaRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  marginTop: 6,
};

const errorText: CSSProperties = {
  display: 'block',
  marginTop: 4,
  fontSize: 13,
  fontWeight: 500,
  color: '#d93025',
};

const groupCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '16px 18px',
  borderRadius: 16,
  background: 'rgba(0,0,0,0.04)',
};

/** Minimal bottom-bordered input used by the CV basics step (reference look). */
function underlineInput(hasError: boolean, fontSize: number): CSSProperties {
  return {
    width: '100%',
    height: fontSize >= 18 ? 44 : 40,
    border: 'none',
    borderBottom: `1px solid ${hasError ? '#d93025' : 'var(--input-border)'}`,
    borderRadius: 0,
    padding: '0 2px',
    fontSize,
    fontWeight: fontSize >= 18 ? 500 : 400,
    color: 'var(--text-primary)',
    background: 'transparent',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
}

function inputStyle(hasError: boolean): CSSProperties {
  return {
    width: '100%',
    height: 48,
    borderRadius: 9999,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: hasError ? '#d93025' : 'var(--input-border)',
    padding: '0 20px',
    fontSize: 15,
    color: 'var(--text-primary)',
    backgroundColor: 'var(--input-bg)',
    boxShadow: 'var(--gem-input-shadow)',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function textareaStyle(hasError: boolean, minHeight: number): CSSProperties {
  return {
    width: '100%',
    minHeight,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: hasError ? '#d93025' : 'var(--input-border)',
    padding: '12px 16px',
    fontSize: 15,
    lineHeight: '22px',
    color: 'var(--text-primary)',
    backgroundColor: 'var(--input-bg)',
    boxShadow: 'var(--gem-input-shadow)',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
  };
}

const sectionRowCard = (enabled: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 16,
  background: enabled ? 'rgba(0,0,0,0.04)' : 'transparent',
  border: enabled ? '1px solid transparent' : '1px dashed var(--input-border)',
});

const checkBtn = (checked: boolean): CSSProperties => ({
  width: 24,
  height: 24,
  borderRadius: 8,
  border: checked ? 'none' : '1.5px solid var(--input-border)',
  background: checked ? 'var(--text-primary)' : 'transparent',
  color: 'var(--bg)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  padding: 0,
});

const arrowBtn = (disabled: boolean): CSSProperties => ({
  width: 30,
  height: 30,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
  opacity: disabled ? 0.35 : 1,
  cursor: disabled ? 'default' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  flexShrink: 0,
});

const sectionFootRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginTop: 8,
};

const suggestBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  paddingInline: 12,
  borderRadius: 9999,
  border: 0,
  background: 'rgba(0,0,0,0.06)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const addSectionBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  alignSelf: 'flex-start',
  marginTop: 2,
  padding: '8px 4px',
  border: 0,
  background: 'transparent',
  color: 'var(--text-primary)',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

